// src/lib/Button.tsx
import { jsx } from "react/jsx-runtime";
var Button = ({
  children,
  onClick,
  variant = "primary",
  size = "medium",
  disabled = false,
  className = "",
  type = "button"
}) => {
  const baseClasses = "inline-flex items-center justify-center font-semibold rounded-xl transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2";
  const variantClasses = {
    primary: "bg-brand-600 text-white hover:bg-brand-700 focus:ring-brand-500",
    secondary: "bg-white text-brand-700 hover:bg-brand-50 focus:ring-brand-400 ring-1 ring-sand-300",
    outline: "border-2 border-brand-600 text-brand-700 hover:bg-brand-600 hover:text-white focus:ring-brand-500",
    danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
  };
  const sizeClasses2 = {
    small: "px-3 py-1.5 text-sm",
    medium: "px-5 py-2.5 text-base",
    large: "px-6 py-3 text-lg"
  };
  const disabledClasses = "opacity-50 cursor-not-allowed";
  const finalClasses = `
    ${baseClasses}
    ${variantClasses[variant]}
    ${sizeClasses2[size]}
    ${disabled ? disabledClasses : ""}
    ${className}
  `.trim();
  return /* @__PURE__ */ jsx(
    "button",
    {
      type,
      onClick: disabled ? void 0 : onClick,
      disabled,
      className: finalClasses,
      children
    }
  );
};

// src/lib/Container.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var Container = ({ className = "", children }) => {
  return /* @__PURE__ */ jsx2("div", { className: `mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`, children });
};

// src/lib/Heading.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var sizeToClass = {
  display: "text-5xl md:text-7xl font-extrabold tracking-tight",
  h1: "text-4xl md:text-6xl font-bold tracking-tight",
  h2: "text-3xl md:text-4xl font-semibold",
  h3: "text-2xl md:text-3xl font-semibold",
  lead: "text-lg md:text-xl text-ink-muted"
};
var Heading = ({ as = "h2", size = "h2", className = "", children }) => {
  const Tag = as;
  return /* @__PURE__ */ jsx3(Tag, { className: `${sizeToClass[size]} ${className}`, children });
};

// src/lib/Section.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
var Section = ({
  className = "",
  innerClassName = "",
  background = "none",
  children
}) => {
  const bgClass = background === "sand" ? "bg-sand-50" : background === "brand" ? "bg-brand-50" : background === "orange-sand" ? "bg-orange-sand-50" : "";
  return /* @__PURE__ */ jsx4("section", { className: `py-14 md:py-20 ${bgClass} ${className}`, children: /* @__PURE__ */ jsx4(Container, { className: innerClassName, children }) });
};

// src/lib/Card.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var Card = ({ className = "", children }) => {
  return /* @__PURE__ */ jsx5("div", { className: `rounded-xl bg-white shadow-soft ring-1 ring-sand-200 ${className}`, children });
};
var CardInner = ({ className = "", children }) => /* @__PURE__ */ jsx5("div", { className: `p-6 md:p-8 ${className}`, children });

// src/lib/Input.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var sizeClasses = {
  small: "h-9 text-sm px-3",
  medium: "h-10 text-base px-3",
  large: "h-12 text-base px-4"
};
var Input = ({
  className = "",
  size = "medium",
  ...props
}) => {
  return /* @__PURE__ */ jsx6(
    "input",
    {
      className: `block w-full rounded-md border border-sand-300 bg-white text-ink placeholder-ink-muted/70 shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-brand-600 ${sizeClasses[size]} ${className}`,
      ...props
    }
  );
};

// src/lib/Carousel.tsx
import { useMemo, useState } from "react";
import { Fragment, jsx as jsx7, jsxs } from "react/jsx-runtime";
function isImageUrl(src) {
  return /\.|\//.test(src);
}
var Carousel = ({
  images,
  className = ""
}) => {
  const [index, setIndex] = useState(0);
  const clampedImages = useMemo(
    () => images.length > 0 ? images : [""],
    [images]
  );
  const atStart = index === 0;
  const atEnd = index === clampedImages.length - 1;
  const goPrev = () => setIndex((i) => Math.max(0, i - 1));
  const goNext = () => setIndex((i) => Math.min(clampedImages.length - 1, i + 1));
  return /* @__PURE__ */ jsxs("div", { className: `relative w-full ${className}`, children: [
    /* @__PURE__ */ jsx7("div", { className: "aspect-square w-full overflow-hidden rounded-xl ring-1 ring-sand-200 bg-white flex items-center justify-center", children: isImageUrl(clampedImages[index]) ? (
      // eslint-disable-next-line @next/next/no-img-element
      /* @__PURE__ */ jsx7(
        "img",
        {
          src: clampedImages[index],
          alt: "",
          className: "h-full w-full object-contain"
        }
      )
    ) : /* @__PURE__ */ jsx7("div", { className: "text-8xl select-none", children: clampedImages[index] || "\u{1F9F8}" }) }),
    clampedImages.length > 1 && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx7(
        "button",
        {
          type: "button",
          onClick: goPrev,
          disabled: atStart,
          className: "absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 text-ink rounded-full h-10 w-10 flex items-center justify-center shadow-soft ring-1 ring-sand-200 disabled:opacity-50",
          "aria-label": "Previous",
          children: "\u2039"
        }
      ),
      /* @__PURE__ */ jsx7(
        "button",
        {
          type: "button",
          onClick: goNext,
          disabled: atEnd,
          className: "absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 text-ink rounded-full h-10 w-10 flex items-center justify-center shadow-soft ring-1 ring-sand-200 disabled:opacity-50",
          "aria-label": "Next",
          children: "\u203A"
        }
      ),
      /* @__PURE__ */ jsx7("div", { className: "mt-3 flex items-center justify-center gap-2", children: clampedImages.map((_, i) => /* @__PURE__ */ jsx7(
        "button",
        {
          type: "button",
          className: `h-2.5 w-2.5 rounded-full ${i === index ? "bg-brand-600" : "bg-sand-300"}`,
          "aria-label": `Go to slide ${i + 1}`,
          onClick: () => setIndex(i)
        },
        i
      )) })
    ] })
  ] });
};

// src/lib/Accordion.tsx
import React2, {
  createContext,
  useContext,
  useId,
  useMemo as useMemo2,
  useState as useState2
} from "react";
import { jsx as jsx8, jsxs as jsxs2 } from "react/jsx-runtime";
var AccordionCtx = createContext(
  void 0
);
var Accordion = ({
  multiple = false,
  className = "",
  children
}) => {
  const [openIds, setOpenIds] = useState2(/* @__PURE__ */ new Set());
  const value = useMemo2(
    () => ({
      openIds,
      multiple,
      toggle: (id) => setOpenIds((prev) => {
        const next = new Set(prev);
        const isOpen = next.has(id);
        if (multiple) {
          if (isOpen) next.delete(id);
          else next.add(id);
        } else {
          next.clear();
          if (!isOpen) next.add(id);
        }
        return next;
      })
    }),
    [openIds, multiple]
  );
  return /* @__PURE__ */ jsx8("div", { className: `rounded-2xl bg-white ring-1 ring-sand-200 ${className}`, children: /* @__PURE__ */ jsx8(AccordionCtx.Provider, { value, children }) });
};
var AccordionItem = ({
  id,
  title,
  defaultOpen = false,
  children
}) => {
  const ctx = useContext(AccordionCtx);
  if (!ctx) throw new Error("AccordionItem must be used inside Accordion");
  const autoId = useId();
  const itemId = id || autoId;
  const buttonId = `${itemId}-button`;
  const regionId = `${itemId}-region`;
  React2.useEffect(() => {
    if (defaultOpen) ctx.toggle(itemId);
  }, []);
  const isOpen = ctx.openIds.has(itemId);
  return /* @__PURE__ */ jsxs2("div", { className: `border-t first:border-t-0 border-sand-200`, children: [
    /* @__PURE__ */ jsxs2(
      "button",
      {
        id: buttonId,
        type: "button",
        "aria-expanded": isOpen,
        "aria-controls": regionId,
        onClick: () => ctx.toggle(itemId),
        className: `w-full text-left px-5 py-4 flex items-center justify-between select-none
                    hover:bg-sand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600`,
        children: [
          /* @__PURE__ */ jsx8("span", { className: "text-ink font-semibold tracking-wide uppercase text-sm", children: title }),
          /* @__PURE__ */ jsx8(
            "svg",
            {
              className: `h-4 w-4 text-ink transition-transform ${isOpen ? "rotate-180" : "rotate-0"}`,
              viewBox: "0 0 20 20",
              fill: "currentColor",
              "aria-hidden": "true",
              children: /* @__PURE__ */ jsx8("path", { d: "M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" })
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx8(
      "div",
      {
        id: regionId,
        role: "region",
        "aria-labelledby": buttonId,
        hidden: !isOpen,
        className: "px-5 py-2 text-ink-muted text-left",
        children
      }
    )
  ] });
};

// src/lib/Footer.tsx
import { jsx as jsx9, jsxs as jsxs3 } from "react/jsx-runtime";
var Footer = ({
  className = "",
  links = []
}) => {
  const year = (/* @__PURE__ */ new Date()).getFullYear();
  return /* @__PURE__ */ jsx9("footer", { className: `bg-sand-50 border-t border-sand-200 ${className}`, children: /* @__PURE__ */ jsxs3(Container, { className: "py-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between", children: [
    /* @__PURE__ */ jsxs3("div", { className: "text-sm text-ink-muted", children: [
      "\xA9 ",
      year,
      " Toddlar. All rights reserved."
    ] }),
    links.length > 0 && /* @__PURE__ */ jsx9("nav", { className: "flex gap-4 text-sm", children: links.map((l) => /* @__PURE__ */ jsx9(
      "a",
      {
        href: l.href,
        className: "text-ink-muted hover:text-ink",
        children: l.label
      },
      l.href + l.label
    )) })
  ] }) });
};
export {
  Accordion,
  AccordionItem,
  Button,
  Card,
  CardInner,
  Carousel,
  Container,
  Footer,
  Heading,
  Input,
  Section
};
